import React, { FC } from "react";
import { CircularProgress, TableCell, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import { useGetReportingHistoryQuery } from "../../repositories/queries/reportings/get-reporting-history.query";
import { formatQuantity, formatQuantityDelta } from "./reporting-quantity.helper";
import { Shades } from "../../style/Colors";


/** Measured from the left: the trailing cell absorbs any columns appended after quantity (e.g. prices). */
const REPORTER_COLSPAN = 3;
const QUANTITY_COLUMN_END = 7;

const useStyles = makeStyles()({
  historyRow: {
    backgroundColor: Shades.gray20,
  },
  historyCell: {
    color: Shades.gray60,
    paddingLeft: "24px",
  },
  emptyCell: {
    color: Shades.gray60,
    textAlign: "center",
    paddingTop: "16px",
    paddingBottom: "16px",
  },
  loadingCell: {
    textAlign: "center",
    paddingTop: "16px",
    paddingBottom: "16px",
  },
  typeLabel: {
    fontWeight: 500,
  },
});

interface IPomReportingHistoryRowProps {
  contractNo: string;
  reportingNo: number;
  positionNo: number;
  unit: string | undefined;
  colSpanTotal: number;
}

export const PomReportingHistoryRow: FC<IPomReportingHistoryRowProps> = (props) => {
  const { contractNo, reportingNo, positionNo, unit, colSpanTotal } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Fetched per reporting rather than per position: every expanded row shares this query key,
  // so React Query issues a single request no matter how many positions are open.
  const { data, isLoading, isError } = useGetReportingHistoryQuery(contractNo, reportingNo, true);

  if (isLoading) {
    return (
      <TableRow className={classes.historyRow}>
        <TableCell colSpan={colSpanTotal} className={classes.loadingCell}>
          <CircularProgress size={24} />
        </TableCell>
      </TableRow>
    );
  }

  // A load failure must not read as "no entries" — on a compliance screen the two mean very
  // different things.
  if (isError) {
    return (
      <TableRow className={classes.historyRow}>
        <TableCell colSpan={colSpanTotal} className={classes.emptyCell}>
          <Typography variant="body2">{t("pom.reportings.history.load_error")}</Typography>
        </TableCell>
      </TableRow>
    );
  }

  const entries = data?.filter((entry) => entry.positionNo === positionNo) ?? [];

  if (entries.length === 0) {
    return (
      <TableRow className={classes.historyRow}>
        <TableCell colSpan={colSpanTotal} className={classes.emptyCell}>
          <Typography variant="body2">{t("pom.reportings.history.no_entries")}</Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {entries.map((entry) => {
        const typeLabel = entry.type === "InitialReporting"
          ? t("pom.reportings.history.initial_reporting")
          : t("pom.reportings.history.correction");

        // Act-as submissions arrive pre-composed from the backend, so there is no separate
        // admin branch here.
        const reporterInfo = t("pom.reportings.history.reported_by", {
          user: entry.reportedBy ?? t("pom.reportings.history.unknown_reporter"),
        });

        return (
          <TableRow key={`${entry.positionNo}-${entry.entryNo}`} className={classes.historyRow}>
            <TableCell className={classes.historyCell}>
              <Typography variant="body2" className={classes.typeLabel}>
                {typeLabel}
              </Typography>
            </TableCell>
            <TableCell className={classes.historyCell}>
              <Typography variant="body2">{dayjs(entry.createdAt).format("DD.MM.YYYY HH:mm")}</Typography>
            </TableCell>
            <TableCell colSpan={REPORTER_COLSPAN} className={classes.historyCell}>
              <Typography variant="body2">{reporterInfo}</Typography>
            </TableCell>
            {/* Aligned with the position row above: the quantity as of this entry sits under
                "Aktuelle Menge", the change it applied under the quantity column. */}
            <TableCell align="right" className={classes.historyCell}>
              <Typography variant="body2">{formatQuantity(entry.resultingQuantity, unit)}</Typography>
            </TableCell>
            <TableCell align="right" className={classes.historyCell}>
              <Typography variant="body2">{formatQuantityDelta(entry.quantityDelta, unit)}</Typography>
            </TableCell>
            <TableCell colSpan={colSpanTotal - QUANTITY_COLUMN_END} className={classes.historyCell} />
          </TableRow>
        );
      })}
    </>
  );
};
